<template>
  <div class="mini-decoration-page">
    <el-card shadow="hover">
      <template slot="header">
        <div class="card-header">
          <span>小程序设置</span>
          <el-button class="btn-primary" :loading="saving" @click="saveConfig">保存配置</el-button>
        </div>
      </template>

      <el-alert
        type="info"
        :closable="false"
        show-icon
        title="轮播图与底部展示图已统一到「轮播图管理」"
        description="请在左侧菜单「营销管理 → 轮播图管理」上传图片；本页维护支付开关、团购核销、营业时间限制、公告与客服中心文案。"
        style="margin-bottom: 20px;"
      />

      <div class="section-block">
        <div class="section-title">支付功能</div>
        <p class="section-hint">关闭后，小程序内所有微信支付入口将不可用，用户发起支付时会提示联系人工客服。</p>
        <el-form label-width="160px" size="small" class="announce-form">
          <el-form-item label="微信支付">
            <el-switch v-model="form.payment_enabled" active-text="开启" inactive-text="关闭" />
            <span class="inline-hint">开启后小程序微信支付正常使用</span>
          </el-form-item>
        </el-form>
      </div>

      <el-divider />

      <div class="section-block">
        <div class="section-title">团购核销</div>
        <p class="section-hint">
          关闭后，小程序同步/添加平台券与普通账号收银台核销均不可用，提示「团购核销功能维护中」；管理员与超级管理员账号仍可核销。
        </p>
        <el-form label-width="160px" size="small" class="announce-form">
          <el-form-item label="团购核销">
            <el-switch v-model="form.groupon_enabled" active-text="开启" inactive-text="关闭" />
            <span class="inline-hint">开启后美团/抖音/饿了么团购核销正常使用</span>
          </el-form-item>
        </el-form>
      </div>

      <el-divider />

      <div class="section-block">
        <div class="section-title">营业时间限制</div>
        <p class="section-hint">
          开启后全天在线，不再因非工作日或营业时间限制下单。关闭后仅工作日、且在门店营业时间内可下单；预约取餐可选今天与后一天中的工作日时段。
        </p>
        <el-form label-width="160px" size="small" class="announce-form">
          <el-form-item label="营业时间限制">
            <el-switch v-model="form.business_hours_unrestricted" active-text="开启" inactive-text="关闭" />
            <span class="inline-hint">开启后全天可下单</span>
          </el-form-item>
        </el-form>
      </div>

      <el-divider />

      <div class="section-block">
        <div class="section-title">公告发布</div>
        <p class="section-hint">配置小程序首页顶部公告条；可选在每次打开小程序时以弹窗形式展示（同一次启动仅弹一次）。</p>
        <el-form label-width="160px" size="small" class="announce-form">
          <el-form-item label="顶部公告栏">
            <el-switch v-model="form.announcement_bar_enabled" active-text="开启" inactive-text="关闭" />
            <span class="inline-hint">关闭后首页不显示顶部橙色公告条</span>
          </el-form-item>
          <el-form-item label="顶部公告内容">
            <el-input
              v-model="form.announcement_bar_text"
              type="textarea"
              :rows="2"
              maxlength="500"
              show-word-limit
              placeholder="例如：公告：新品上市，欢迎品尝！"
            />
          </el-form-item>
          <el-form-item label="打开时弹窗公告">
            <el-switch v-model="form.announcement_popup_enabled" active-text="开启" inactive-text="关闭" />
            <span class="inline-hint">开启后，用户打开小程序进入首页时会弹出公告</span>
          </el-form-item>
          <el-form-item label="弹窗公告内容">
            <el-input
              v-model="form.announcement_popup_text"
              type="textarea"
              :rows="4"
              maxlength="2000"
              show-word-limit
              placeholder="留空则使用「顶部公告内容」作为弹窗正文"
            />
          </el-form-item>
          <el-form-item label="弹窗图">
            <el-switch v-model="form.announcement_popup_image_enabled" active-text="开启" inactive-text="关闭" />
            <span class="inline-hint">开启后打开小程序时展示 3:4 弹窗图（同一次启动仅弹一次）</span>
          </el-form-item>
          <el-form-item label="弹窗图上传">
            <div class="img-row">
              <el-input v-model="form.announcement_popup_image" placeholder="/uploads/mini-decoration/..." />
              <el-upload
                action="#"
                :show-file-list="false"
                :http-request="uploadPopupImage"
                accept="image/*"
              >
                <el-button size="small" class="btn-secondary">上传</el-button>
              </el-upload>
              <el-button
                v-if="form.announcement_popup_image"
                size="small"
                type="text"
                @click="form.announcement_popup_image = ''"
              >清除</el-button>
            </div>
            <p class="field-hint">建议比例 3:4（竖图），展示时按 3:4 裁切；建议宽度 ≥ 750px</p>
            <div v-if="form.announcement_popup_image" class="popup-img-preview-wrap">
              <el-image
                :src="previewUrl(form.announcement_popup_image)"
                fit="cover"
                class="popup-img-preview"
              />
            </div>
          </el-form-item>
        </el-form>
      </div>

      <el-divider />

      <div class="section-block">
        <div class="section-title">客服中心</div>
        <p class="section-hint">
          对应小程序页面 <code>pages/user/service/index</code>（我的 → 客服中心）。电话请在此配置，不要写死在代码里。
        </p>
        <el-form label-width="160px" size="small" class="announce-form">
          <el-form-item label="页头副标题">
            <el-input
              v-model="form.service_hero_sub"
              maxlength="120"
              show-word-limit
              placeholder="我们随时为您解答订餐、配送与账户问题"
            />
          </el-form-item>
          <el-form-item label="客服电话（拨号）">
            <el-input
              v-model="form.service_phone"
              maxlength="20"
              placeholder="仅数字，如 13958677072"
            />
            <span class="inline-hint">用于 wx.makePhoneCall，勿含空格/横线</span>
          </el-form-item>
          <el-form-item label="电话展示文案">
            <el-input
              v-model="form.service_phone_display"
              maxlength="32"
              placeholder="如 139-5867-7072；留空则显示拨号号码"
            />
          </el-form-item>
          <el-form-item label="工作时间">
            <el-input v-model="form.service_work_hours" maxlength="40" placeholder="9:00-18:00" />
          </el-form-item>
          <el-form-item label="联系区块标题">
            <el-input v-model="form.service_contact_title" maxlength="40" placeholder="联系我们" />
          </el-form-item>
          <el-form-item label="联系区块正文">
            <el-input
              v-model="form.service_contact_body"
              type="textarea"
              :rows="3"
              maxlength="2000"
              show-word-limit
              placeholder="门店地址、合作与客服说明等"
            />
          </el-form-item>
          <el-form-item label="微信客服二维码">
            <div class="img-row">
              <el-input v-model="form.service_wechat_qr_image" placeholder="/uploads/mini-decoration/..." />
              <el-upload
                action="#"
                :show-file-list="false"
                :http-request="uploadServiceQr"
                accept="image/*"
              >
                <el-button size="small" class="btn-secondary">上传</el-button>
              </el-upload>
            </div>
            <el-image
              v-if="form.service_wechat_qr_image"
              :src="previewUrl(form.service_wechat_qr_image)"
              fit="cover"
              class="qr-preview"
            />
          </el-form-item>
          <el-form-item label="二维码标题">
            <el-input v-model="form.service_wechat_qr_caption" maxlength="30" placeholder="微信客服" />
          </el-form-item>
        </el-form>
      </div>
    </el-card>
  </div>
</template>

<script>
import { formatApiError } from '../utils/apiError'

export default {
  name: 'MiniDecoration',
  data() {
    return {
      saving: false,
      form: {
        banner_images: [],
        footer_image: '',
        announcement_bar_enabled: true,
        announcement_bar_text: '',
        announcement_popup_enabled: false,
        announcement_popup_text: '',
        announcement_popup_image_enabled: false,
        announcement_popup_image: '',
        service_hero_sub: '',
        service_phone: '',
        service_phone_display: '',
        service_work_hours: '9:00-18:00',
        service_contact_title: '联系我们',
        service_contact_body: '',
        service_wechat_qr_image: '',
        service_wechat_qr_caption: '微信客服',
        payment_enabled: true,
        groupon_enabled: true,
        business_hours_unrestricted: false
      }
    }
  },
  mounted() {
    this.loadConfig()
  },
  methods: {
    previewUrl(path) {
      const s = String(path || '').trim()
      if (!s) return ''
      if (/^https?:\/\//i.test(s)) return s
      const base = (this.$api && this.$api.defaults && this.$api.defaults.baseURL) || ''
      const origin = String(base).replace(/\/api\/?$/, '')
      return origin + (s.startsWith('/') ? s : '/' + s)
    },
    async uploadServiceQr(param) {
      try {
        const formData = new FormData()
        formData.append('file', param.file)
        formData.append('kind', 'banner')
        const resp = await this.$api.post('/admin/mini-decoration/upload?kind=banner', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        if (resp && resp.code === 200 && resp.data && resp.data.url) {
          this.form.service_wechat_qr_image = resp.data.url
          this.$message.success('上传成功')
        }
      } catch (e) {
        this.$message.error(formatApiError(e, '上传失败'))
      }
    },
    async uploadPopupImage(param) {
      try {
        const formData = new FormData()
        formData.append('file', param.file)
        formData.append('kind', 'banner')
        const resp = await this.$api.post('/admin/mini-decoration/upload?kind=banner', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        if (resp && resp.code === 200 && resp.data && resp.data.url) {
          this.form.announcement_popup_image = resp.data.url
          const w = Number(resp.data.width) || 0
          const h = Number(resp.data.height) || 0
          if (w > 0 && h > 0) {
            const ratio = w / h
            const target = 3 / 4
            if (Math.abs(ratio - target) / target > 0.12) {
              this.$message.warning(
                `已上传（${w}×${h}）。建议 3:4 竖图，当前将按 3:4 裁切展示`
              )
            } else {
              this.$message.success('上传成功')
            }
          } else {
            this.$message.success('上传成功')
          }
        }
      } catch (e) {
        this.$message.error(formatApiError(e, '上传失败'))
      }
    },
    applyData(d) {
      if (!d || typeof d !== 'object') return
      this.form.announcement_bar_enabled = d.announcement_bar_enabled !== false
      this.form.announcement_bar_text = d.announcement_bar_text != null ? String(d.announcement_bar_text) : ''
      this.form.announcement_popup_enabled = !!d.announcement_popup_enabled
      this.form.announcement_popup_text =
        d.announcement_popup_text != null ? String(d.announcement_popup_text) : ''
      this.form.announcement_popup_image_enabled = !!d.announcement_popup_image_enabled
      this.form.announcement_popup_image =
        d.announcement_popup_image != null ? String(d.announcement_popup_image) : ''
      this.form.service_hero_sub = d.service_hero_sub != null ? String(d.service_hero_sub) : ''
      this.form.service_phone = d.service_phone != null ? String(d.service_phone) : ''
      this.form.service_phone_display =
        d.service_phone_display != null ? String(d.service_phone_display) : ''
      this.form.service_work_hours =
        d.service_work_hours != null ? String(d.service_work_hours) : '9:00-18:00'
      this.form.service_contact_title =
        d.service_contact_title != null ? String(d.service_contact_title) : '联系我们'
      this.form.service_contact_body =
        d.service_contact_body != null ? String(d.service_contact_body) : ''
      this.form.service_wechat_qr_image =
        d.service_wechat_qr_image != null ? String(d.service_wechat_qr_image) : ''
      this.form.service_wechat_qr_caption =
        d.service_wechat_qr_caption != null ? String(d.service_wechat_qr_caption) : '微信客服'
      this.form.payment_enabled = d.payment_enabled !== false
      this.form.groupon_enabled = d.groupon_enabled !== false
      this.form.business_hours_unrestricted = d.business_hours_unrestricted === true
    },
    async loadConfig() {
      try {
        const resp = await this.$api.get('/admin/mini-decoration')
        if (resp && resp.code === 200 && resp.data) {
          this.applyData(resp.data)
        }
      } catch (e) {
        this.$message.error(formatApiError(e, '加载小程序设置失败'))
      }
    },
    async saveConfig() {
      this.saving = true
      try {
        const payload = {
          banner_images: [],
          footer_image: '',
          announcement_bar_enabled: !!this.form.announcement_bar_enabled,
          announcement_bar_text: this.form.announcement_bar_text,
          announcement_popup_enabled: !!this.form.announcement_popup_enabled,
          announcement_popup_text: this.form.announcement_popup_text,
          announcement_popup_image_enabled: !!this.form.announcement_popup_image_enabled,
          announcement_popup_image: this.form.announcement_popup_image,
          service_hero_sub: this.form.service_hero_sub,
          service_phone: this.form.service_phone,
          service_phone_display: this.form.service_phone_display,
          service_work_hours: this.form.service_work_hours,
          service_contact_title: this.form.service_contact_title,
          service_contact_body: this.form.service_contact_body,
          service_wechat_qr_image: this.form.service_wechat_qr_image,
          service_wechat_qr_caption: this.form.service_wechat_qr_caption,
          payment_enabled: !!this.form.payment_enabled,
          groupon_enabled: !!this.form.groupon_enabled,
          business_hours_unrestricted: !!this.form.business_hours_unrestricted
        }
        const resp = await this.$api.put('/admin/mini-decoration', payload)
        if (resp && resp.code === 200) {
          this.$message.success('保存成功')
          if (resp.data) this.applyData(resp.data)
        }
      } catch (e) {
        this.$message.error(formatApiError(e, '保存失败'))
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.mini-decoration-page {
  padding: 20px;
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.section-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
}
.section-hint {
  color: #909399;
  font-size: 13px;
  margin-bottom: 16px;
}
.section-hint code {
  font-size: 12px;
  background: #f5f7fa;
  padding: 1px 6px;
  border-radius: 4px;
}
.inline-hint {
  margin-left: 12px;
  color: #909399;
  font-size: 12px;
}
.img-row {
  display: flex;
  gap: 10px;
  align-items: center;
  width: 100%;
}
.img-row .el-input {
  flex: 1;
}
.qr-preview {
  margin-top: 10px;
  width: 120px;
  height: 120px;
  border-radius: 8px;
  border: 1px solid #ebeef5;
}
.field-hint {
  margin: 8px 0 0;
  color: #909399;
  font-size: 12px;
  line-height: 1.5;
}
.popup-img-preview-wrap {
  margin-top: 12px;
  width: 150px;
  /* 3:4 */
  height: 200px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #ebeef5;
  background: #f5f7fa;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.08);
}
.popup-img-preview {
  width: 100%;
  height: 100%;
  display: block;
}
</style>
